import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { empresasApi, getDisplayMessage } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/lib/AuthContext';

function planSlugFromNome(nome) {
  if (!nome) return '';
  return nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '');
}

function slugPlanoEquivaleUrlEPlano(slugUrl, slugPlanoNome) {
  const a = String(slugUrl || '').trim().toLowerCase();
  const b = String(slugPlanoNome || '');
  if (!a || !b) return false;
  if (a === b) return true;
  const st = new Set(['start', 'starter']);
  return st.has(a) && st.has(b);
}

function precoMensalDoPlano(p) {
  if (p == null) return NaN;
  const v = p.preco_mensal ?? p.precoMensal;
  return Number(v);
}

function parsePrecoMensalQuery(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const n = parseFloat(String(raw).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function planoIdPorPreco(planosNorm, preco) {
  if (!Array.isArray(planosNorm) || preco == null) return '';
  const matches = planosNorm
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => {
      const pm = precoMensalDoPlano(p);
      return Number.isFinite(pm) && Math.abs(pm - preco) < 0.01;
    });
  if (matches.length === 0) return '';
  matches.sort((a, b) => a.i - b.i);
  return matches[0].p.id;
}

export default function AssinaturaPlanoPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { empresa, checkAppState, logout } = useAuth();
  const slugParam = planSlugFromNome(searchParams.get('plano') || '');
  const precoParam = useMemo(() => parsePrecoMensalQuery(searchParams.get('preco_mensal')), [searchParams]);
  const checkoutQuery = (searchParams.get('checkout') || '').toLowerCase();

  const { data: planos = [], isLoading, error } = useQuery({
    queryKey: ['empresas-planos'],
    queryFn: () => empresasApi.planos(),
  });

  const planosNorm = useMemo(
    () =>
      (Array.isArray(planos) ? planos : []).map((p) => ({
        ...p,
        _slug: planSlugFromNome(p.nome),
      })),
    [planos]
  );

  const planoIdResolvido = useMemo(() => {
    if (!planosNorm.length) return '';
    if (precoParam != null) {
      const id = planoIdPorPreco(planosNorm, precoParam);
      if (id) return id;
    }
    if (slugParam) {
      const m = planosNorm.find((p) => slugPlanoEquivaleUrlEPlano(slugParam, p._slug));
      if (m) return m.id;
    }
    if (empresa?.plano?.id) return empresa.plano.id;
    return '';
  }, [planosNorm, slugParam, precoParam, empresa?.plano?.id]);

  const selected = planosNorm.find((p) => p.id === planoIdResolvido);

  const [tipo, setTipo] = useState('mensal');
  const [msg, setMsg] = useState('');

  const planoMismatch =
    empresa?.status === 'awaiting_payment' &&
    empresa?.plano?.id &&
    planoIdResolvido &&
    empresa.plano.id !== planoIdResolvido;

  const mutation = useMutation({
    mutationFn: () => empresasApi.assinarPlano(planoIdResolvido, tipo),
    onSuccess: async (data) => {
      setMsg('');
      await queryClient.invalidateQueries({ queryKey: ['uso'] });
      await queryClient.invalidateQueries({ queryKey: ['faturas'] });
      const url = data?.pagamento_url;
      if (url) {
        window.location.assign(url);
      } else {
        setMsg('Não foi possível obter o link de pagamento. Tente novamente ou contate o suporte.');
      }
    },
    onError: (err) => {
      setMsg(getDisplayMessage(err, 'Não foi possível iniciar o pagamento.'));
    },
  });

  useEffect(() => {
    if (checkoutQuery !== 'success') return undefined;
    let tries = 0;
    const id = setInterval(async () => {
      tries += 1;
      await checkAppState();
      if (tries >= 40) {
        clearInterval(id);
        setMsg((m) =>
          m ||
          'Confirmando pagamento… Se o acesso não liberar em instantes, atualize a página ou aguarde o retorno do Asaas.'
        );
      }
    }, 2000);
    return () => clearInterval(id);
  }, [checkoutQuery, checkAppState]);

  useEffect(() => {
    if (checkoutQuery === 'success' && empresa?.status && empresa.status !== 'awaiting_payment') {
      setSearchParams({}, { replace: true });
      navigate('/Dashboard', { replace: true });
    }
  }, [checkoutQuery, empresa?.status, navigate, setSearchParams]);

  useEffect(() => {
    if (checkoutQuery === 'cancel') {
      setMsg('Checkout cancelado. Você pode tentar de novo quando quiser.');
      setSearchParams({}, { replace: true });
    }
  }, [checkoutQuery, setSearchParams]);

  const bloqueadoPagamento = empresa?.status === 'awaiting_payment';

  return (
    <div className="max-w-lg mx-auto py-8 px-4">
      <Card>
        <CardHeader>
          <CardTitle>Pagamento do plano</CardTitle>
          <CardDescription>
            {bloqueadoPagamento
              ? 'Conclua o pagamento com cartão de crédito na página segura do Asaas para liberar o acesso ao sistema. Assinaturas recorrentes não aceitam Pix neste fluxo.'
              : 'O pagamento é concluído na página segura do Asaas.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{getDisplayMessage(error, 'Erro ao carregar planos.')}</AlertDescription>
            </Alert>
          )}
          {msg && (
            <Alert variant={msg.includes('suporte') || msg.includes('Confirmando') ? 'default' : 'destructive'}>
              <AlertDescription>{msg}</AlertDescription>
            </Alert>
          )}
          {checkoutQuery === 'success' && empresa?.status === 'awaiting_payment' && (
            <Alert>
              <AlertDescription>Confirmando pagamento com o servidor…</AlertDescription>
            </Alert>
          )}
          {empresa?.status === 'awaiting_payment' && (
            <Alert variant="default" className="border-amber-200 bg-amber-50/80">
              <AlertDescription className="space-y-3 text-amber-950">
                <p>
                  Se você <strong>não</strong> concluiu o cadastro da empresa (ou quer cadastrar <strong>outro CNPJ</strong>),
                  pode ser que esta sessão esteja associada a um cadastro anterior. Saia e abra o link do plano de novo.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-amber-300 bg-white"
                  onClick={() => {
                    logout(false);
                    const q = typeof window !== 'undefined' ? window.location.search || '' : '';
                    window.location.assign(`/Registro${q}`);
                  }}
                >
                  Sair e ir para o cadastro
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {planoMismatch && (
            <Alert variant="destructive">
              <AlertDescription>
                O link não corresponde ao plano do seu cadastro. Use o mesmo link enviado após o registro ou abra o
                e-mail de boas-vindas.
              </AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : !planoIdResolvido || !selected ? (
            <p className="text-sm text-muted-foreground">
              Não foi possível identificar o plano. Acesse esta página pelo link recebido após o cadastro ou com os
              parâmetros <code className="text-xs">plano</code> ou <code className="text-xs">preco_mensal</code> na
              URL.
            </p>
          ) : (
            <>
              <div className="rounded-lg border border-border bg-primary/5 p-4 space-y-1">
                <div className="font-medium text-lg">{selected.nome}</div>
                <div className="text-sm text-muted-foreground">
                  {selected.usuarios_inclusos} usuários · a partir de{' '}
                  {Number.isFinite(precoMensalDoPlano(selected))
                    ? precoMensalDoPlano(selected).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                    : '—'}{' '}
                  /mês
                </div>
              </div>

              <div className="space-y-2">
                <Label>Cobrança</Label>
                <RadioGroup value={tipo} onValueChange={setTipo} className="flex gap-4">
                  <label className="flex cursor-pointer items-center gap-2">
                    <RadioGroupItem value="mensal" id="tipo-mensal" />
                    <span>Mensal</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2">
                    <RadioGroupItem value="anual" id="tipo-anual" />
                    <span>Anual</span>
                  </label>
                </RadioGroup>
                {tipo === 'anual' && selected.preco_anual != null && (
                  <p className="text-xs text-muted-foreground">
                    Valor anual:{' '}
                    {Number(selected.preco_anual).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </p>
                )}
              </div>

              <Button
                type="button"
                className="w-full"
                disabled={!planoIdResolvido || mutation.isPending || !!planoMismatch}
                onClick={() => mutation.mutate()}
              >
                {mutation.isPending ? 'Redirecionando…' : 'Continuar para pagamento'}
              </Button>
            </>
          )}

          {empresa?.status !== 'awaiting_payment' && (
            <p className="text-center text-sm text-muted-foreground">
              <Link to="/Dashboard" className="text-primary hover:underline">
                Voltar ao painel
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
